import { useEffect, useId, useState } from 'react';
import {
  acknowledgeTeardown,
  claimMount,
  forceClaimMount,
  isCurrentClaimant,
  onTeardownAcknowledged
} from '~/utilities/mfe/mfeMountGate';

const DEFAULT_MAX_WAIT_MS = 2000;

/**
 * Delays rendering a per-page MFE until any previous instance of the same module
 * has torn down — see mfeMountGate.ts. Falls back to allowing the mount after
 * maxWaitMs so a stuck module can't permanently block navigation. The claim
 * check runs in an effect, not during render, so it sees the outgoing instance's
 * teardown (guaranteed by React to run first) rather than a stale entry.
 */
export default function useMfeMountGate(
  moduleName: string,
  maxWaitMs: number = DEFAULT_MAX_WAIT_MS
) {
  const instanceId = useId();
  const [canMount, setCanMount] = useState(false);

  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout> | undefined;

    if (claimMount(moduleName, instanceId)) {
      setCanMount(true);
    } else {
      timeoutId = setTimeout(() => {
        forceClaimMount(moduleName, instanceId);
        setCanMount(true);
      }, maxWaitMs);
    }

    const unsubscribe = onTeardownAcknowledged(moduleName, () => {
      if (claimMount(moduleName, instanceId)) {
        setCanMount(true);
      } else {
        // A forced reassignment elsewhere may have superseded our own claim.
        setCanMount(isCurrentClaimant(moduleName, instanceId));
      }

      // Clear unconditionally — a stale timer here could later force-reclaim and
      // steal ownership back from whichever instance this notification just settled on.
      clearTimeout(timeoutId);
    });

    return () => {
      unsubscribe();
      clearTimeout(timeoutId);
    };
  }, [instanceId, maxWaitMs, moduleName]);

  useEffect(
    () => () => acknowledgeTeardown(moduleName, instanceId),
    [instanceId, moduleName]
  );

  return canMount;
}
